import React, { useState, useEffect } from 'react';
import { login, loginWithGoogle } from '../../services/auth';
import { AnalyticsEvents } from '../../services/analytics';
import FormField from '../FormField';
import Input from '../Input';
import LottieAnimation from './LottieAnimation';
import ProxyImage from '../ProxyImage';
import './Auth.css';
interface LoginProps {
  onSuccess: () => void;
  onSwitchToRegister: () => void;
  onForgotPassword: () => void;
  onVerifyCode?: (email: string) => void;
}
const Login: React.FC<LoginProps> = ({ onSuccess, onSwitchToRegister, onForgotPassword, onVerifyCode }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  // Manejar callback de Google OAuth
  // NOTA: Este manejo es solo para errores, el éxito se maneja en AuthWrapper
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const error = urlParams.get('error');
    // Limpiar parámetros de la URL inmediatamente si hay alguno
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // Si hay success=google_oauth, no hacer nada aquí (AuthWrapper lo maneja)
    if (success === 'google_oauth') {
      return;
    }
    if (error) {
      // La URL ya fue limpiada arriba
      let errorMessage = 'Error al iniciar sesión con Google.';
      if (error === 'oauth_error') {
        errorMessage = 'Ocurrió un error durante la autenticación con Google.';
      } else if (error === 'oauth_failed') {
        errorMessage = 'No se pudo completar la autenticación con Google.';
      } else if (error === 'session_error') {
        errorMessage = 'Error al crear la sesión. Por favor, intenta nuevamente.';
      }
      setErrors({ general: errorMessage });
    }
  }, [onSuccess]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    // Validación
    const newErrors: Record<string, string> = {};
    if (!formData.email.trim()) {
      newErrors.email = 'Email requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }
    if (!formData.password) {
      newErrors.password = 'Contraseña requerida';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setLoading(true);
    try {
      const result = await login(formData);
      if (result.success) {
        // Track login exitoso
        AnalyticsEvents.userLogin('email');
        onSuccess();
      } else {
        // Si requiere verificación, redirigir a la pantalla de verificación
        if ((result as any).requiresVerification && onVerifyCode) {
          setErrors({ general: result.error || 'Debes verificar tu email antes de iniciar sesión.' });
          setTimeout(() => {
            onVerifyCode(formData.email);
          }, 2000);
        } else {
          setErrors({ general: result.error || 'Error al iniciar sesión' });
        }
      }
    } catch (error) {
      setErrors({ general: 'Error de conexión. Por favor, intenta nuevamente.' });
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        <div className="auth-lottie-column">
          <div className="auth-logo">
            <a 
              href="https://smartcityexposantiagodelestero.com/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ProxyImage 
                src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png" 
                alt="Smart City Expo Santiago del Estero" 
              />
            </a>
          </div>
          <LottieAnimation />
          <div className="auth-partners-image">
            <ProxyImage 
              src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2026/01/Instituciones-Colaboradoras-Web-scaled-e1767776957958.png" 
              alt="Instituciones Colaboradoras" 
            />
          </div>
        </div>
        <div className="auth-form-column">
          <div className="auth-card">
            <div className="auth-logo-mobile">
              <a 
                href="https://smartcityexposantiagodelestero.com/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ProxyImage 
                  src="https://smartcityexposantiagodelestero.com/wp-content/uploads/2025/03/Logo-SCESDE26-Negro-e1751508867641-1536x213.png" 
                  alt="Smart City Expo Santiago del Estero" 
                />
              </a>
            </div>
        <h2 className="auth-title">Iniciar Sesión</h2>
        <p className="auth-subtitle">Accede a tu perfil</p>
        {errors.general && (
          <div className="auth-message error">
            {errors.general}
          </div>
        )}
        <button
          type="button"
          onClick={loginWithGoogle}
          className="auth-button-google"
          disabled={loading}
        >
          <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuar con Google
        </button>
        <div className="auth-divider">
          <span>o</span>
        </div>
        <form onSubmit={handleSubmit} className="auth-form">
          <FormField label="Correo electrónico" required error={errors.email}>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="correo@ejemplo.com"
              error={!!errors.email}
              disabled={loading}
            />
          </FormField>
          <FormField label="Contraseña" required error={errors.password}>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Ingresa tu contraseña"
              error={!!errors.password}
              disabled={loading}
            />
          </FormField>
          <div className="auth-forgot">
            <button 
              type="button" 
              onClick={onForgotPassword}
              className="auth-link"
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>
        <div className="auth-switch">
          <p>¿No tienes cuenta? <button type="button" onClick={onSwitchToRegister} className="auth-link">Regístrate</button></p>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Login;
